import React, { useEffect, useState } from "react"
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Grid, Paper, Stack, TextField, Typography } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined"
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined"
import { LoadingButton } from "@mui/lab"
import { Link, useHistory } from "react-router-dom"
import Vista from "../../components/Vista"
import Multifile from "../../components/Multifile"
import { useLocalStorage } from "../../hooks/useLocalStorage"
import { useMessages } from "../../hooks/useMessages"
import Servo from "../../services/servo"

const empty = { placas: "", economico: "", modelo: "", marca: "", year: "", vin: "", descripcion: "", idEmpresa: null, idSucursal: null, tarjetaCirculacion: "", fechaPoliza: "" }
const cardStyle = { p: { xs: 2.5, md: 3.5 }, borderRadius: 3, borderColor: "#e1e8ef", boxShadow: "0 3px 16px #172b4d05" }

export default function Detail({ match }) {
  const id = match?.params?.id
  const editado = !!id
  const history = useHistory()
  const { setMessages } = useMessages()
  const [permiso] = useLocalStorage("permiso", null)
  const canSave = !!(permiso?.carros & (editado ? 4 : 2))
  const [form, setForm] = useState(empty)
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(editado)
  const [loaded, setLoaded] = useState(!editado)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")
  const [catalogs, setCatalogs] = useState({ empresas: [], sucursales: [] })
  useEffect(() => {
    let active = true
    setLoaded(!id)
    setLoading(!!id)
    setError("")
    if (id) Servo.get(`carros?expand=archivos&idCarro=${encodeURIComponent(id)}`).then(res => {
      if (!active) return
      const car = res?.cuerpo?.resultado?.[0]
      if (!car) throw new Error("No se encontró la unidad o no tienes acceso.")
      setForm({ ...empty, ...car })
      setFiles(car.archivos || [])
      setLoaded(true)
    }).catch(e => { if (active) setError(e.message || "No se pudo cargar la unidad.") })
      .finally(() => { if (active) setLoading(false) })
    else { setForm(empty); setFiles([]) }
    return () => { active = false }
  }, [id])
  useEffect(() => {
    let active = true
    for (const key of ["empresas", "sucursales"]) {
      Servo.get(key).then(res => {
        if (active) setCatalogs(previous => ({ ...previous, [key]: res?.cuerpo?.resultado || [] }))
      }).catch(() => {})
    }
    return () => { active = false }
  }, [])
  const change = (key, value) => setForm(previous => ({ ...previous, [key]: value }))
  const guardar = async () => {
    if (!canSave || !loaded || uploading || saving) return
    setSaving(true); setError("")
    try {
      const res = await Servo.post("carros/guardar", { ...form, archivos: files, ...(id ? { idCarro: id } : {}) }, "carros", true, history)
      if (res?.cuerpo) setMessages(res.cuerpo)
    } catch (_) { setError("No se pudo guardar. Revisa la conexión y vuelve a intentarlo.") }
    finally { setSaving(false) }
  }
  const field = (key, label, required = false, type = "text", md = 4) => <Grid item xs={12} sm={6} md={md} key={key}>
    <TextField fullWidth label={label} required={required} type={type}
      value={type === "date" ? (form[key] || "").slice(0, 10) : form[key] || ""}
      disabled={!canSave || saving} onChange={event => change(key, event.target.value)}
      InputLabelProps={{ shrink: true }} />
  </Grid>
  const catalog = (key, attribute, label, labelKey) => {
    const options = catalogs[key]
    const selected = options.find(option => String(option[attribute]) === String(form[attribute])) ||
      (form[attribute] ? { [attribute]: form[attribute], [labelKey]: `${label} #${form[attribute]}` } : null)
    return <Grid item xs={12} sm={6} md={4}>
      <Autocomplete options={selected && !options.includes(selected) ? [selected, ...options] : options} value={selected}
        disabled={!canSave || saving} getOptionLabel={option => option[labelKey] || ""}
        isOptionEqualToValue={(a, b) => String(a[attribute]) === String(b[attribute])}
        onChange={(_, value) => change(attribute, value?.[attribute] ?? null)}
        renderInput={params => <TextField {...params} label={label} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />} />
    </Grid>
  }
  return <Vista style={{ display: "block", background: "#f3f6fa", minHeight: "100vh" }}>
    <Box sx={{ maxWidth: 1360, mx: "auto", px: { xs: 2, md: 4 }, pt: 3, pb: 5,
      color: "#23374b", "& .MuiInputBase-root": { borderRadius: 2, background: "#fff" },
      "& .MuiButton-root": { textTransform: "none", fontWeight: 600, borderRadius: 2 } }}>
      <Button component={Link} to="/carros" startIcon={<ArrowBackIcon />} sx={{ mb: 2, px: 0 }}>Volver a carros</Button>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ bgcolor: "#e2effa", color: "#157cc1", p: 1.5, borderRadius: 3, display: "flex" }}><DirectionsCarOutlinedIcon fontSize="large" /></Box>
        <Box sx={{ flex: 1 }}><Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>CONTROL DE FLOTA</Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 25, md: 30 } }}>{editado ? "Editar carro" : "Nuevo carro"}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Información de la unidad y expediente documental.</Typography></Box>
        {form.economico && <Chip label={form.economico} sx={{ bgcolor: "#e2effa", color: "#146ca5", fontWeight: 700 }} />}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box sx={{ p: 8, textAlign: "center" }}><CircularProgress /></Box> : <Stack spacing={3}>
        <Paper variant="outlined" sx={cardStyle}>
          <Typography variant="h6" sx={{ fontWeight: 650, mb: .5 }}>Datos de la unidad</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Identificación y características del vehículo.</Typography>
          <Grid container spacing={3}>
            {field("placas", "Placas", true)}{field("economico", "Número económico", true)}{field("vin", "VIN", true)}
            {field("marca", "Marca", true)}{field("modelo", "Modelo", true)}{field("year", "Año", true)}
          </Grid>
        </Paper>
        <Paper variant="outlined" sx={cardStyle}>
          <Typography variant="h6" sx={{ fontWeight: 650, mb: 3 }}>Asignación y documentación</Typography>
          <Grid container spacing={3}>
            {catalog("empresas", "idEmpresa", "Empresa", "nombre")}{catalog("sucursales", "idSucursal", "Sucursal", "nombreSucursal")}
            {field("tarjetaCirculacion", "Fecha de tarjeta de circulación", false, "date")}
            {field("fechaPoliza", "Fecha de póliza", false, "date")}
            {field("descripcion", "Descripción", false, "text", 8)}
          </Grid>
        </Paper>
        <Paper variant="outlined" sx={cardStyle}>
          <Multifile files={files} setFiles={setFiles} firebaseFolder="PDFs_Unidad" loading={uploading} setLoading={setUploading}
            readOnly={!canSave || !loaded || saving} detachOnly />
        </Paper>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">{canSave ? "Guarda para conservar los datos y los documentos de la unidad." : "Acceso de consulta a la unidad."}</Typography>
          <Stack direction="row" spacing={1.5}>
            <Button component={Link} to="/carros" color="inherit" variant="outlined">Volver</Button>
            {canSave && <LoadingButton variant="contained" startIcon={<SaveOutlinedIcon />} loading={saving} disabled={uploading || !loaded} onClick={guardar}
              sx={{ px: 3, py: 1.3, boxShadow: "none", bgcolor: "#157cc1" }}>Guardar cambios</LoadingButton>}
          </Stack>
        </Stack>
      </Stack>}
    </Box>
  </Vista>
}
